import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledInvoiceTabContainer} from './StyledInvoicesTab';
import TableFilters from '../../tabComponents/TableFilters';
import NoSearchResults from '../../../../NoSearchResults';
import Table from '../../../../Table';
import {amountHelpers, dateHelpers, invoicesHelpers} from '../../../../../utils/helpers';

const dateColumnProps = {
  render: (value) => value ? dateHelpers.getDateWithMonth(value) : '',
  sortDirections: ['descend', 'ascend'],
  sorter: true,
  width: 120,
};

const InvoicesTab = ({
  data = [],
  edit = false,
  empty,
  loading = false,
  unmatched = false,
  onFilter,
  openDetails,
  tableHeaderContent,
  tableFiltersProps,
  tableProps,
  selectedRowIndex,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [query, setQuery] = useState({
    search: ''
  });
  const [emptyDetails, setEmptyDetails] = useState({
    text: '',
    enabled: false
  });

  const trans = (key) => t(`invoices:${key}`);

  const isEnabledEmpty = useMemo(() => {
    return empty && query.search === '' && data.length === 0 && !loading;
  }, [data, empty, loading, query]);

  useEffect(() => {
    const isEmptyList = data.length === 0;
    const search = query?.search || '';
    if (search !== '' && isEmptyList) {
      setEmptyDetails({
        text: search,
        enabled: true
      });
    } else if (emptyDetails.enabled && !isEmptyList) {
      setEmptyDetails({...emptyDetails, enabled: false});
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnFilter = (filterParams) => {
    setQuery({...query, ...filterParams});
    onFilter && onFilter(filterParams);
  }

  const getColumns = () => {
    return [
      {
        dataIndex: 'date',
        title: `${t('invoice')} ${t('date')}`,
        ...dateColumnProps
      },
      {
        dataIndex: 'vendor',
        title: t('vendor')
      },
      {
        dataIndex: 'uploaded_by',
        title: trans('uploadedBy')
      },
      {
        dataIndex: 'type',
        render: (_, record) => (
          <span className='invoice-type'>
            {invoicesHelpers.getInvoicePaymentType(record)}
          </span>
        ),
        title: t('type')
      },
      {
        dataIndex: unmatched ? 'due_date' : 'paid_date',
        title: `${t(unmatched ? 'due' : 'payment')} ${t('date')}`,
        ...dateColumnProps
      },
      {
        align: 'right',
        dataIndex: 'amount',
        render: (amount, record) => (
          <span className='amount'>
            {amount ? amountHelpers.getAmountWithCurrencyCode(amount, undefined, record?.currency) : ''}
          </span>
        ),
        title: t('amount'),
      },
      {
        dataIndex: 'payment_status',
        render: invoicesHelpers.getInvoiceStatusMark,
        sorter: true,
        title: t('status')
      },
      {
        align: 'right',
        dataIndex: 'source_type',
        render: invoicesHelpers.getInvoiceSourceTypeTag,
      },
      {
        dataIndex: 'match',
        render: (_, record) => invoicesHelpers.getInvoiceMatchType(record),
        title: '',
        width: 64
      }
    ];
  }

  const columns = getColumns();

  return (
    <StyledInvoiceTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        onFilter={handleOnFilter}
      />
      {isEnabledEmpty ? empty : (
        <>
          {emptyDetails.enabled ? (
            <NoSearchResults
              spinning={loading}
              value={emptyDetails.text}
            />
          ) : (
            <>
              {tableHeaderContent}
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey='id'
                {...tableProps}
              />
            </>
          )}
        </>
      )}
    </StyledInvoiceTabContainer>
  );
}

InvoicesTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  edit: PropTypes.bool,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  unmatched: PropTypes.bool,
  onFilter: PropTypes.func,
  selectedRowIndex: PropTypes.any,
  tableFiltersProps: PropTypes.any,
  tableHeaderContent: PropTypes.any,
  tableProps: PropTypes.object
}

export default InvoicesTab;
